import React from 'react';
import { GAME_LABELS, GAME_XP } from '../hooks/useGameChallenge';

const CONFETTI_COLORS = ['#EAB308','#e10600','#ffffff','#f97316','#84cc16','#38bdf8'];
const CONFETTI_COUNT  = 28;

const Confetti = () => (
  <div className="pointer-events-none" aria-hidden>
    <style>{`
      @keyframes cf-fall{
        0%  {transform:translateY(-80px) rotate(0deg)   scale(1);   opacity:1}
        80% {opacity:1}
        100%{transform:translateY(105vh) rotate(540deg) scale(.6); opacity:0}
      }
    `}</style>
    {Array.from({length: CONFETTI_COUNT}).map((_,i) => {
      const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
      const left  = `${(i * 3.7 + 2) % 100}%`;
      const delay = `${(i * 0.09).toFixed(2)}s`;
      const dur   = `${1.8 + (i % 7) * 0.22}s`;
      const w     = i % 3 === 0 ? 10 : i % 3 === 1 ? 7 : 5;
      return (
        <div key={i} style={{
          position:'absolute', top:0, left, width:w, height:w*1.6,
          background:color, borderRadius:2,
          animation:`cf-fall ${dur} ${delay} ease-in both`,
        }}/>
      );
    })}
  </div>
);

const Avatar = ({ u, size=16, ring }) => (
  <div style={{
    width:size*4, height:size*4, borderRadius:'50%', overflow:'hidden', flexShrink:0,
    border:`3px solid ${ring}`, boxShadow:`0 0 20px ${ring}55`,
  }}>
    {u?.avatar
      ? <img src={u.avatar} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
      : <div style={{width:'100%',height:'100%',background:ring+'22',display:'flex',alignItems:'center',justifyContent:'center',fontSize:size*1.4,fontWeight:900,color:ring}}>
          {u?.username?.[0]?.toUpperCase()?? '?'}
        </div>
    }
  </div>
);

const ChallengeResultOverlay = ({ iWon, isDraw, me, opponent, myScore, opponentScore, formatScore, game, onClose }) => {
  const xp        = GAME_XP[game] ?? 1000;
  const gameLabel = GAME_LABELS[game] ?? game;

  const accentColor  = iWon ? '#EAB308' : isDraw ? '#71717a' : '#e10600';
  const bgGrad       = iWon
    ? 'radial-gradient(ellipse at 50% 0%, rgba(234,179,8,.18) 0%, rgba(12,12,16,.98) 55%)'
    : isDraw
      ? 'radial-gradient(ellipse at 50% 0%, rgba(113,113,122,.12) 0%, rgba(12,12,16,.98) 55%)'
      : 'radial-gradient(ellipse at 50% 0%, rgba(225,6,0,.14) 0%, rgba(12,12,16,.98) 55%)';

  const headline = iWon ? 'VICTOIRE !' : isDraw ? 'ÉGALITÉ' : 'DÉFAITE';
  const emoji    = iWon ? '🏆' : isDraw ? '🤝' : '😤';

  const myFmt  = myScore       !== null ? formatScore(myScore)       : null;
  const oppFmt = opponentScore !== null ? formatScore(opponentScore) : null;

  return (
    <div
      onClick={onClose}
      style={{
        position:'fixed', inset:0, zIndex:9999, display:'flex',
        alignItems:'center', justifyContent:'center',
        background:'rgba(0,0,0,.82)', backdropFilter:'blur(6px)',
        cursor:'pointer',
      }}
    >
      <style>{`
        @keyframes ov-in  {from{opacity:0;transform:scale(.9) translateY(20px)}to{opacity:1;transform:scale(1) translateY(0)}}
        @keyframes ov-glow{0%,100%{text-shadow:0 0 40px ${accentColor}88}50%{text-shadow:0 0 80px ${accentColor}cc}}
        @keyframes ov-xp  {from{opacity:0;transform:scale(.5) rotate(-8deg)}to{opacity:1;transform:scale(1) rotate(-3deg)}}
      `}</style>

      {/* Confetti only for winner */}
      {iWon && (
        <div style={{position:'absolute',inset:0,overflow:'hidden',pointerEvents:'none'}}>
          <Confetti />
        </div>
      )}

      {/* Card */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position:'relative', width:'min(420px,92vw)',
          borderRadius:24, border:`2px solid ${accentColor}55`,
          background:bgGrad, padding:'32px 24px 24px',
          animation:'ov-in .4s cubic-bezier(.34,1.56,.64,1) both',
          boxShadow:`0 0 60px ${accentColor}22, 0 20px 60px rgba(0,0,0,.6)`,
        }}
      >
        {/* Top stripe */}
        <div style={{
          position:'absolute',top:0,left:0,right:0,height:4,borderRadius:'24px 24px 0 0',
          background:`linear-gradient(90deg,${accentColor} 0%,transparent 80%)`,
        }}/>

        {/* Game label */}
        <p style={{textAlign:'center',color:'#71717a',fontSize:10,fontWeight:700,letterSpacing:2,textTransform:'uppercase',marginBottom:12}}>
          {gameLabel}
        </p>

        {/* Big headline */}
        <p style={{
          textAlign:'center', fontSize:42, fontWeight:900, color:accentColor,
          letterSpacing:2, lineHeight:1, marginBottom:4,
          animation:'ov-glow 2s ease infinite',
        }}>
          {emoji} {headline}
        </p>
        <p style={{textAlign:'center',color:'#a1a1aa',fontSize:13,marginBottom:24}}>
          {iWon ? `Tu as battu ${opponent?.username} !` : isDraw ? `Égalité avec ${opponent?.username}` : `${opponent?.username} t'a battu cette fois`}
        </p>

        {/* Avatars + scores */}
        <div style={{display:'flex',alignItems:'center',gap:12,justifyContent:'center',marginBottom:20}}>
          {/* Me */}
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:8,flex:1}}>
            <Avatar u={me}  ring={iWon ? '#EAB308' : isDraw ? '#71717a' : '#52525b'} />
            <p style={{color:'#ffffff',fontSize:12,fontWeight:800,textAlign:'center'}}>{me?.username ?? 'Moi'}</p>
            <div style={{
              padding:'6px 14px',borderRadius:12,
              background: iWon ? 'rgba(234,179,8,.12)' : 'rgba(255,255,255,.06)',
              border:`1px solid ${iWon ? 'rgba(234,179,8,.4)' : 'rgba(255,255,255,.1)'}`,
            }}>
              <p style={{color: iWon ? '#EAB308' : '#ffffff', fontSize:18, fontWeight:900, textAlign:'center', margin:0}}>
                {myFmt ?? '—'}
              </p>
              {iWon && <p style={{color:'#EAB308',fontSize:9,textAlign:'center',fontWeight:700,marginTop:2}}>🏆 GAGNANT</p>}
            </div>
          </div>

          {/* VS */}
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:4,flexShrink:0}}>
            <p style={{color:'#3f3f46',fontSize:18,fontWeight:900}}>VS</p>
          </div>

          {/* Opponent */}
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:8,flex:1}}>
            <Avatar u={opponent} ring={!iWon && !isDraw ? '#e10600' : '#52525b'} />
            <p style={{color:'#a1a1aa',fontSize:12,fontWeight:800,textAlign:'center'}}>{opponent?.username}</p>
            <div style={{
              padding:'6px 14px',borderRadius:12,
              background: !iWon && !isDraw ? 'rgba(225,6,0,.10)' : 'rgba(255,255,255,.04)',
              border:`1px solid ${!iWon && !isDraw ? 'rgba(225,6,0,.35)' : 'rgba(255,255,255,.08)'}`,
            }}>
              <p style={{color: !iWon && !isDraw ? '#e10600' : '#71717a', fontSize:18, fontWeight:900, textAlign:'center', margin:0}}>
                {oppFmt ?? '—'}
              </p>
              {!iWon && !isDraw && <p style={{color:'#e10600',fontSize:9,textAlign:'center',fontWeight:700,marginTop:2}}>🏆 GAGNANT</p>}
            </div>
          </div>
        </div>

        {/* XP badge */}
        {iWon && (
          <div style={{
            textAlign:'center',marginBottom:20,
            animation:'ov-xp .5s .3s cubic-bezier(.34,1.56,.64,1) both',
          }}>
            <span style={{
              display:'inline-block',background:'linear-gradient(135deg,#EAB308,#f97316)',
              color:'#000',fontWeight:900,fontSize:20,padding:'6px 20px',
              borderRadius:50,letterSpacing:1,boxShadow:'0 4px 20px rgba(234,179,8,.4)',
            }}>
              +{xp} XP 🎉
            </span>
          </div>
        )}

        {/* Buttons */}
        <div style={{display:'flex',gap:8}}>
          <button onClick={onClose} style={{
            flex:1,padding:'11px 0',borderRadius:14,fontWeight:800,fontSize:14,
            background: iWon ? 'linear-gradient(135deg,#EAB308,#f97316)' : 'rgba(255,255,255,.08)',
            color: iWon ? '#000' : '#ffffff',
            border:`1px solid ${iWon ? 'transparent' : 'rgba(255,255,255,.12)'}`,
            cursor:'pointer',
          }}>
            Continuer
          </button>
        </div>

      </div>
    </div>
  );
};

export default ChallengeResultOverlay;
